const safeString = (val) => val ?? '';

export const generateCcmClinicalNotesTemplate = (values) => {
  const {
    data,
    allergiesYes,
    mentalYes,
    nervousYes,
    worryYes,
    interestYes,
    downYes,
    anxietyYes,
    depressionYes,
    prevmonthgoalsYes,
    conditionsYes,
    medicalHistoryYes,
    pastAppointmentYes,
    labsYes,
    upcomingAppointmentYes,
    nonComplaintYes,
    activemedicationsYes,
    medChangesYes,
    lastReadingYes,
    vitalsYes,
    appetiteYes,
    nutritionYes,
    waterYes,
    painYes,
    skinYes,
    respiratoryYes,
    sleepYes,
    bowelYes,
    bladderYes,
    dmeYes,
    safetyYes,
    resourcesYes,
    educationYes,
    extraYes,
  } = values;
  let text = '';

  if (data.appttype) {
    text = `Successful ${safeString(data.appttype)} appointment`;
  } else {
    text = `Successful appointment`;
  }

  if (data.age && data.gender) {
    text += ` of a ${safeString(data.age)} year old ${safeString(
      data.gender,
    )} patient.`;
  } else if (data.age) {
    text += ` of a ${safeString(data.age)} year old patient.`;
  } else if (data.gender) {
    text += ` of a ${safeString(data.gender)} patient.`;
  } else {
    text += ` of a patient.`;
  }

  if (data.program && data.doctor) {
    text += ` The patient is receiving ${safeString(
      data.program,
    )} services for the management of their chronic conditions under the care of ${safeString(
      data.doctor,
    )}.`;
  } else if (data.program) {
    text += ` The patient is receiving ${safeString(
      data.program,
    )} services for the management of their chronic conditions.`;
  } else if (data.doctor) {
    text += ` The patient is under the care of ${safeString(data.doctor)}.`;
  }

  if (conditionsYes && data.icdCodes) {
    text += `The patient has been diagnosed with the following conditions: ${data.icdCodes}. `;
  } else {
    text += `The patient has been diagnosed with no conditions. `;
  }

  if (medicalHistoryYes && data.dx1) {
    text += `The patient presents with a past medical history of ${data.dx1}. `;
  }

  if (allergiesYes && data.allergies) {
    text += `The patient presents with the following allergies: ${data.allergies}. `;
  }

  if (data.patientcaregiver) {
    text += `The agent spoke with the ${data.patientcaregiver} when completing this appointment. `;
  }

  if (mentalYes && data.mentalstatus) {
    text += `Patient is alert, oriented, and coherent x${data.mentalstatus}. `;
  }

  text += '\n\n';

  if (nervousYes) {
    text += `When asked if the patient is feeling nervous, anxious, or on edge, they responded ${data.nervous}. `;
  }
  if (worryYes) {
    text += `When asked if the patient is unable to stop or control worrying, they responded ${data.worry}. `;
  }
  if (interestYes) {
    text += `When asked if the patient experiences little interest or pleasure in doing things, they responded ${data.interest}. `;
  }
  if (data.interestNote) text += `${data.interestNote}. `;
  if (downYes) {
    text += `When asked if the patient is feeling down, depressed, or hopeless, they responded ${data.down}. `;
  }
  if (data.downNote) text += `${data.downNote}. `;

  if (anxietyYes && !data.anxietyNote) {
    text += `The patient’s anxiety score is ${data.anxiety}. `;
  } else if (anxietyYes && data.anxietyNote) {
    text += `The patient’s anxiety score is ${data.anxiety}. ${data.anxietyNote}. `;
  } else if (data.anxietyNote) {
    text += `The patient’s anxiety score is ${data.anxietyNote}. `;
  }

  if (depressionYes && !data.depressionNote) {
    text += `The patient’s depression score is ${data.depression}. `;
  } else if (depressionYes && data.depressionNote) {
    text += `The patient’s depression score is ${data.depression}. ${data.depressionNote}. `;
  } else if (data.depressionNote) {
    text += `The patient’s depression score is ${data.depressionNote}. `;
  }

  text += '\n\n';

  if (data.patientcaregiver) {
    if (pastAppointmentYes && data.pastapptanwsers) {
      text += `When asked about recent past appointments, the ${data.patientcaregiver} communicated they had an appointment with their ${data.pastapptanwsers}. `;
    } else {
      text += `The ${data.patientcaregiver} did not communicate information about past appointments at this time. `;
    }
  }

  if (labsYes && data.labs) {
    text += `Currently, they are pending ${data.labs} results at this time. `;
  }
  if (data.labsNote) {
    text += `Pending labs are ${data.labsNote}. `;
  }

  if (data.patientcaregiver) {
    if (upcomingAppointmentYes) {
      text += `When asked about upcoming appointments, the ${data.patientcaregiver} communicated they have an appointment with their ${data.upcomingapptanwsers}. `;
    } else {
      text += `The ${data.patientcaregiver} did not communicate information about any upcoming appointments at this time. `;
    }
  }

  if (data.patientcaregiver || data.medications) {
    if (data.patientcaregiver) {
      text += `The ${safeString(data.patientcaregiver)} verbalized they `;
    } else {
      text += `The patient verbalized they `;
    }

    if (data.medications) {
      text += `${safeString(
        data.medications,
      )} taking medications as prescribed. `;
    } else {
      text += `are taking medications as prescribed. `;
    }
  }
  if (nonComplaintYes) {
    text += `${data.medwhy}. `;
  }

  if (activemedicationsYes) {
    if (data.patientmedications) {
      text += `The Agent reviewed and confirmed the patient is taking the following medications: ${data.patientmedications} at this time. `;
    } else if (data.patientcaregiver) {
      text += `The ${data.patientcaregiver} was not willing to review the medications they are actively taking at this time. `;
    }
  }

  if (data.patientcaregiver) {
    if (medChangesYes && data.medchanges) {
      text += `The ${data.patientcaregiver} verbalized the following changes in their medication: ${data.medchanges}. `;
    } else {
      text += `The ${data.patientcaregiver} verbalized there are no changes in their medication at this time. `;
    }
  }

  if (lastReadingYes && data.rpm) {
    text += `Last RPM Reading: ${data.rpm}. `;
  }

  if (vitalsYes) {
    text += `${data.vitals}. `;
  }

  text += '\n\n';

  if (appetiteYes && data.appetite) {
    text += `The patient verbalized their appetite is ${data.appetite} over the past 30 days. `;
  } else if (data.patientcaregiver) {
    text += `The ${data.patientcaregiver} was not willing to discuss appetite at this time. `;
  }

  if (nutritionYes) {
    text += `The patient verbalized they regularly consume ${data.nutrition} over the past 30 days. `;
  } else if (data.patientcaregiver) {
    text += `The ${data.patientcaregiver} was not willing to discuss nutrition at this time. `;
  }

  if (waterYes) {
    text += `The patient verbalized they usually drink ${data.water} over the past 30 days. `;
  } else if (data.patientcaregiver) {
    text += `The ${data.patientcaregiver} was not willing to discuss their water intake at this time. `;
  }
  if (data.waterNote) text += `${data.waterNote}. `;

  if (painYes) {
    if (data.patientcaregiver) {
      text += `The ${safeString(
        data.patientcaregiver,
      )} states they are experiencing pain.`;
    } else {
      text += `The patient states they are experiencing pain.`;
    }

    if (data.location && data.severity) {
      text += ` The pain is in the ${safeString(
        data.location,
      )} with a severity of ${safeString(data.severity)}`;
    } else if (data.location) {
      text += ` The pain is in the ${safeString(data.location)}`;
    } else if (data.severity) {
      text += ` The pain has a severity of ${safeString(data.severity)}`;
    } else {
      text += ` The pain severity was reported`;
    }

    if (data.severityNote) {
      text += ` ${safeString(data.severityNote)}`;
    }

    text += `.`;

    if (data.painmeds) {
      text += ` The patient is taking ${safeString(
        data.painmeds,
      )} to manage their pain.`;
    }
  } else if (data.patientcaregiver) {
    text += `The ${data.patientcaregiver} states they have no pain at this time. `;
  }
  if (data?.painNote) {
    text += `Other pain is ${data.painNote}. `;
  }

  if (skinYes && data.skin) {
    text += `The patient has the following skin concerns: ${data.skin} at this time. `;
  } else if (data.patientcaregiver) {
    text += `The ${data.patientcaregiver} was not willing to discuss skin concerns at this time. `;
  }

  if (respiratoryYes) {
    text += `The patient has the following respiratory concerns: ${data.respiratory} at this time. `;
  } else if (data.patientcaregiver) {
    text += `The ${data.patientcaregiver} was not willing to discuss respiratory concerns at this time. `;
  }

  if (data.patientcaregiver) {
    if (sleepYes) {
      text += `The ${data.patientcaregiver} states they sleep on average ${data.sleep} over the past 30 days. `;
    } else {
      text += `The ${data.patientcaregiver} was not willing to discuss their sleep patterns at this time. `;
    }
  }

  text += '\n\n';

  if (bowelYes) {
    text += `Patient has the following bowel concerns: ${data.bowel} at this time. `;
  } else if (data.patientcaregiver) {
    text += `The ${data.patientcaregiver} was not willing to discuss their bowel concerns at this time. `;
  }

  if (bladderYes) {
    text += `Patient has the following bladder concerns: ${data.bladder} at this time. `;
  } else if (data.patientcaregiver) {
    text += `The ${data.patientcaregiver} was not willing to discuss their bladder concerns at this time. `;
  }

  if (dmeYes) {
    text += `The patient uses the following DMEs: ${data.DME} at this time. `;
  } else if (data.patientcaregiver) {
    text += `The ${data.patientcaregiver} was not willing to discuss their use of DMEs or ADLs at this time. `;
  }

  if (safetyYes) {
    text += `There are the following safety concerns: ${data.safety} at this time. `;
  } else if (data.patientcaregiver) {
    text += `The ${data.patientcaregiver} was not willing to discuss their safety concerns at this time. `;
  }

  const fallType = data.falltype ? ` ${data.falltype}` : '';
  const ERdetail = data.ERdetail ? ` ${data.ERdetail}` : '';
  if (data.falls || fallType) {
    if (data.falls) {
      text += `The patient verbalized they had ${safeString(data.falls)}`;
    } else {
      text += `The patient verbalized they had a fall`;
    }

    if (fallType) {
      text += `${safeString(fallType)}`;
    }

    text += ` in the past 30 days. `;
  }
  if (data.fallsNote) text += `${data.fallsNote}. `;
  if (data.fallsTypeNote) text += `${data.fallsTypeNote}. `;

  if (data.emergency || ERdetail) {
    if (data.emergency) {
      text += `The patient verbalized they had ${safeString(data.emergency)}`;
    } else {
      text += `The patient verbalized they had an emergency`;
    }

    if (ERdetail) {
      text += `${safeString(ERdetail)}`;
    }

    text += ` in the past 30 days. `;
  }
  if (data.emergencyNote) text += `${data.emergencyNote}. `;

  text += '\n\n';

  if (prevmonthgoalsYes) {
    if (data.patientcaregiver) {
      text += `The Agent reviewed last month’s goals and the ${safeString(
        data.patientcaregiver,
      )} verbalized `;
    } else {
      text += `The Agent reviewed last month’s goals and the patient verbalized `;
    }

    if (data.prevmonthgoals) {
      text += `${safeString(data.prevmonthgoals)}. `;
    }
  }
  if (data.prevmonthgoalsNote) text += `${data.prevmonthgoalsNote}. `;

  if (data.goals) {
    text += `${data.goals} `;
  }

  if (resourcesYes) {
    text += `The following resources were provided to the patient: ${data.resources}. `;
  }

  const sendEducationNote = data.sendEducationNote
    ? ` of ${safeString(data.sendEducationNote)}`
    : '';
  const sendEducationHowNote = data.sendEducationHowNote
    ? ` ${safeString(data.sendEducationHowNote)}`
    : '';

  if (educationYes && data.education && data.sendEducationHow) {
    text += `The patient would like education${sendEducationNote} on ${safeString(
      data.education,
    )} sent by ${safeString(data.sendEducationHow)}. `;
  } else if (educationYes && data.education) {
    text += `The patient would like education on ${safeString(
      data.education,
    )}, but the method of delivery was not specified. `;
  } else if (educationYes && data.sendEducationHow) {
    text += `The patient would like education sent by ${safeString(
      data.sendEducationHow,
    )}, but the topic was not specified. `;
  } else {
    text += `The patient declined having education sent to them at this time. `;
  }

  if (sendEducationHowNote) {
    text += `${sendEducationHowNote}. `;
  }

  if (data.patientcaregiver) {
    text += `The Agent educated the patient on practicing preventative care (such as diet modifications) for sustained health using the teach back method to improve patient understanding and adherence, decrease call backs and canceled appointments and to improve patient satisfaction and outcomes. The ${data.patientcaregiver} verbalized understanding of the education and the agent was able to answer any and all questions at this time. `;
  }

  if (extraYes) {
    text += `${data.extra}. `;
  }

  text += `The patient agreed to follow up contact on a regular basis. `;

  if (data.nextapptdate) {
    const nextappttime = data.nextappttime ? ` at ${data.nextappttime}` : ``;
    text += `Their next scheduled appointment is ${data.nextapptdate}${nextappttime}. `;
    if (data.nextApptWillBeBy) {
      text += `Their next appointment will be by ${data.nextApptWillBeBy}. `;
    }
  }

  text += `The patient was advised to call Accuhealth at 1-866-836-7260 or text 78616 should they have any questions, concerns or medical needs before our next scheduled follow up. `;

  return text;
};
